import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import { useFromExplore, useSearchParamsObject, useAppDispatch, useAppSelector } from 'app/hooks';
import { useGetSchoolsQuery } from 'services/exploreApi';
import { navigateTop } from 'common/utils/index';
import { SessionState, setFilters } from 'features/session/sessionSlice';

import styles from './Explore.module.scss';
import Filters, { ExploreSort } from './ExploreFilters';
import { CommonHash, ApiList, School } from 'app/types';
import ExploreSelector from './ExploreSelector';
import SchoolCard from './school/SchoolCard';
import Pagination from 'common/components/Pagination/Pagination';

interface PaginationInterface {
  pages: number;
  current: number;
  total: number;
}

const defaultPage: PaginationInterface = {
  pages: 0,
  current: 1,
  total: 0,
};

const Explore: FunctionComponent = () => {
  const { filters: profileFilters, profile } = useAppSelector(
    ({ session }: { session: SessionState }) => session
  );
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [searchFilters, setSearchFilters] = useState<{ [k: string]: any }>(profileFilters);
  const [appliedFilters, setAppliedFilters] = useState<{ [k: string]: any }>(profileFilters);
  const [schoolPagination, setSchoolPagination] = useState<PaginationInterface>(defaultPage);
  const location = useLocation();
  const [degreeSelected, setDegreeSelected] = useState<string | null>(
    location.pathname.split('/').pop()?.toLowerCase() || 'masters'
  );
  const navigate = useNavigate();

  const [searchParams] = useSearchParamsObject();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const dispatch = useAppDispatch();
  const [allowFetching, setAllowFetching] = useState<boolean>(false);

  useFromExplore(true);

  const {
    data: schools,
    isLoading: isSchoolsLoading,
    isFetching: isSchoolsFetching,
    isUninitialized: isSchoolsUninitialized,
  } = useGetSchoolsQuery({
    ...appliedFilters,
    partner_schools: window.location.href.includes('top-schools'),
    page: schoolPagination.current,
    degree_type: degreeSelected,
  });
  const filtersTimer = useRef<NodeJS.Timeout>();

  const navigateUp = (windowOnly = false) => {
    if (isMobile || windowOnly) {
      navigateTop(isMobile ? 0 : 200);
    } else {
      navigateTop(200);
      navigateTop(0, 0, 'exploreSchoolList');
      navigateTop(0, 0, 'exploreProgramList');
    }
  };

  const onFilterChange = (key: string, value: any) => {
    if (value === undefined || value === null || value === '') {
      setSearchFilters(({ [key]: _, ...rest }) => rest);
    } else {
      setSearchFilters((sf) => ({ ...sf, [key]: value }));
    }
  };

  const onAppliedChange = (key: any, value: any) => {
    if (value === undefined || value === null || value === '') {
      setSearchFilters(({ [key]: _, ...rest }) => rest);
      setAppliedFilters(({ [key]: _, ...rest }) => rest);
    } else {
      setSearchFilters((sf) => ({ ...sf, [key]: value }));
      setAppliedFilters((sf) => ({ ...sf, [key]: value }));
    }
  };

  const applyFilters = (close = true) => {
    setAppliedFilters(searchFilters);
    setSchoolPagination((sp) => ({ ...sp, current: 1 }));
    navigateUp();
    if (close) {
      setShowFilters(false);
    }
  };

  const cleanFilters = () => {
    const newFilters: CommonHash = new Object();
    Object.keys(appliedFilters).map((k: string) => {
      k.includes('order') ? (newFilters[k] = appliedFilters[k]) : null;
    });
    return newFilters;
  };

  const clearFilters = () => {
    setSearchFilters(cleanFilters());
    setAppliedFilters(cleanFilters());
    setSchoolPagination((sp) => ({ ...sp, current: 1 }));
    setShowFilters(false);
  };

  const changePageSchools = (page: number) => {
    setSchoolPagination((sp) => ({ ...sp, current: page }));
    navigateUp();
  };

  const filtersApplied = true;

  useEffect(() => {
    if (
      !['masters', 'bachelors'].includes(location.pathname.split('/').pop()?.toLowerCase() || '')
    ) {
      navigate('/explore/masters');
    } else {
      setDegreeSelected(location.pathname.split('/').pop()?.toLowerCase() || 'masters');
    }
  }, [location.pathname]);

  useEffect(() => {
    for (const [key, value] of Object.entries(searchParams)) {
      if (key === 'subject_id') {
        setSearchFilters((f) => ({ ...f, [key]: [Number(value)] }));
        setAppliedFilters((f) => ({ ...f, [key]: [Number(value)] }));
      }
    }
  }, []);

  useEffect(() => {
    setAppliedFilters(profileFilters);
  }, [profileFilters]);

  useEffect(() => {
    if (schools?.meta) setSchoolPagination(schools.meta);
  }, [schools]);

  useEffect(() => {
    if (searchFilters.query !== appliedFilters.query) {
      clearTimeout(filtersTimer.current);
      filtersTimer.current = setTimeout(() => applyFilters(false), 800);
    }
    return () => clearTimeout(filtersTimer.current);
  }, [searchFilters.query]);

  useEffect(() => {
    document.title = 'Unify | Explore';
  }, []);

  useEffect(() => {
    dispatch(setFilters(appliedFilters));
    appliedFilters.query && setSearchFilters(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    if (!isSchoolsUninitialized && !allowFetching && !isSchoolsLoading) {
      setAllowFetching(true);
    }
  }, [isSchoolsLoading]);

  return (
    <>
      {!degreeSelected ? (
        <ExploreSelector />
      ) : (
        <div className={styles.explore}>
          <section className="bg-dark-primary text-center p-relative">
            <div className="d-flex justify-content-center py-3">
              <Link
                to="/profile"
                className="text-white fw-bold m-0 text-decoration-none animate__animated animate__delay-2s animate__headShake"
              >
                <i className="d-inline-block d-md-none bi bi-robot"></i>{' '}
                {profile ? 'Edit your answers in our ' : 'Match with your perfect school '}
                {profile ? (
                  <span className="d-none d-md-inline-block">
                    <i className="bi bi-robot"></i> AI powered tool
                  </span>
                ) : (
                  <span className="d-none d-md-inline-block">
                    using our <i className="bi bi-robot"></i> AI powered tool{' '}
                  </span>
                )}
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </section>
          <Filters
            onFilterChange={onFilterChange}
            onAppliedChange={onAppliedChange}
            setShowFilters={setShowFilters}
            applyFilters={applyFilters}
            clearFilters={clearFilters}
            showFilters={showFilters}
            appliedFilters={appliedFilters}
            searchFilters={searchFilters}
            searchParams={searchParams}
            degreeSelected={degreeSelected}
          />
          <section className={`${styles.uaSchoolList} container-fluid bg-lighter-primary d-flex`}>
            <div className="container">
              <div className="row justify-content-center justify-content-sm-start pt-4 pb-1 align-items-center d-flex flex-row">
                {((!isSchoolsLoading && !allowFetching) ||
                  (allowFetching && !isSchoolsFetching)) && (
                  <p className="m-0 text-secondary w-auto ms-sm-1">
                    Showing <span className="fw-semibold">{schoolPagination.total}</span> schools:{' '}
                  </p>
                )}
                <ExploreSort
                  onFilterChange={onAppliedChange}
                  searchFilters={appliedFilters}
                  onModal={false}
                />
              </div>
              {isSchoolsLoading || (allowFetching && isSchoolsFetching) ? (
                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mt-0 pb-5">
                  <div className="col ">
                    <div className="skeleton skeleton-card rounded-3 w-100"></div>
                  </div>
                  <div className="col ">
                    <div className="skeleton skeleton-card rounded-3 w-100"></div>
                  </div>
                  <div className="col ">
                    <div className="skeleton skeleton-card rounded-3 w-100"></div>
                  </div>
                </div>
              ) : (
                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mt-0 pb-5">
                  {schools &&
                    (schools as ApiList<School>).data?.map((school) => (
                      <SchoolCard school={school} key={school.id} appliedFilters={appliedFilters} />
                    ))}
                </div>
              )}
              <Pagination
                className={styles.pagination}
                {...schoolPagination}
                onChange={changePageSchools}
              />
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default Explore;
